import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Monitor, Loader2, RefreshCw, Pencil, LogOut, Trash2, Wifi, WifiOff, AlertTriangle,
} from 'lucide-react';

import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { skeletonKeys } from '../lib/listKeys';
import { fmtCurrency } from '../lib/localeUtils';
import { VarianceBadge } from '../components/shift/HandoverRow';
import { Link } from 'react-router-dom';

const STATUS = {
  online: { label: 'Online', icon: Wifi, className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' },
  idle: { label: 'Idle', icon: WifiOff, className: 'bg-slate-500/10 text-slate-600 border-slate-500/30' },
  quiet: { label: 'Gone quiet', icon: AlertTriangle, className: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  signed_out: { label: 'Signed out', icon: LogOut, className: 'bg-rose-500/10 text-rose-600 border-rose-500/30' },
  unknown: { label: 'Never seen', icon: WifiOff, className: 'bg-slate-500/10 text-slate-600 border-slate-500/30' },
};

const lastContact = (minutes) => {
  if (minutes === null || minutes === undefined) return 'never';
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS[status] || STATUS.unknown;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs ${cfg.className}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </Badge>
  );
};

// Last few drawer counts, so an owner sees cash discrepancies next to the tills.
const RecentHandovers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/shift/handovers', { params: { limit: 8 } })
      .then(({ data }) => setRows(data.handovers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="terminal-handovers-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recent shift handovers</CardTitle>
        <CardDescription>
          What each cashier counted in the drawer against what the till expected.{' '}
          <Link to="/shift-handovers" className="text-primary underline" data-testid="all-handovers-link">See all</Link>
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Closed</TableHead>
              <TableHead>Cashier</TableHead>
              <TableHead>Till</TableHead>
              <TableHead className="text-right">Expected</TableHead>
              <TableHead className="text-right">Counted</TableHead>
              <TableHead>Variance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? skeletonKeys(2, 'handover-skeleton').map((key) => (
              <TableRow key={key}><TableCell colSpan={6}><div className="h-9 bg-muted rounded animate-pulse" /></TableCell></TableRow>
            )) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm" data-testid="terminal-handovers-empty">
                  No drawer counts recorded yet.
                </TableCell>
              </TableRow>
            ) : rows.map((h) => (
              <TableRow key={h.handover_id} data-testid={`terminal-handover-${h.handover_id}`}>
                <TableCell className="text-sm whitespace-nowrap">{new Date(h.ended_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{h.user_name}</TableCell>
                <TableCell className="text-sm">{h.terminal_label || 'Browser session'}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtCurrency(h.expected_cash)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtCurrency(h.counted_total)}</TableCell>
                <TableCell><VarianceBadge handover={h} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default function TerminalsPage() {  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(null);
  const [label, setLabel] = useState('');
  const [confirm, setConfirm] = useState(null); // { terminal, action: 'sign-out' | 'forget' }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/terminals');
      setTerminals(data.terminals || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load terminals');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLabel = async () => {
    setBusy(true);
    try {
      await api.put(`/terminals/${renaming.terminal_id}`, { label });
      toast.success('Terminal renamed');
      setRenaming(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rename terminal');
    }
    setBusy(false);
  };

  const runAction = async () => {
    const { terminal, action } = confirm;
    setBusy(true);
    try {
      if (action === 'sign-out') {
        await api.post(`/terminals/${terminal.terminal_id}/sign-out`);
        toast.success(`${terminal.label} signed out`);
      } else {
        await api.delete(`/terminals/${terminal.terminal_id}`);
        toast.success(`${terminal.label} removed from the register`);
      }
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="terminals-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" /> Terminal Register
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every desktop till that has signed in. A terminal keeps its session for up to
            14 days so it can sell through an outage — if one is lost or replaced, sign it
            out here so that saved session can&apos;t be used.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="terminals-refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card data-testid="terminals-table-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {loading ? 'Loading…' : `${terminals.length} terminal${terminals.length === 1 ? '' : 's'}`}
          </CardTitle>
          <CardDescription>
            Last contact is how long ago the terminal reached the server — you decide what counts as lost.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Terminal</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Signed in as</TableHead>
                <TableHead>App</TableHead>
                <TableHead className="text-right">Today</TableHead>
                <TableHead>Last contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                skeletonKeys(3, 'terminal-skeleton').map((key) => (
                  <TableRow key={key}><TableCell colSpan={8}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : terminals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="terminals-empty">
                    <Monitor className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p>No desktop terminals have signed in yet.</p>
                    <p className="text-xs mt-1">Install the DutiLyne Desktop Terminal and sign in — it registers itself automatically.</p>
                  </TableCell>
                </TableRow>
              ) : terminals.map((t) => (
                <TableRow key={t.terminal_id} data-testid={`terminal-row-${t.terminal_id}`}>
                  <TableCell>
                    <p className="font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground font-mono">{t.terminal_id}</p>
                  </TableCell>
                  <TableCell className="text-sm">{t.branch_name}</TableCell>
                  <TableCell className="text-sm">
                    <p>{t.user_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{t.user_email || ''}</p>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <p>{t.app_version || '—'}</p>
                    <p>{t.platform || ''}</p>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums" data-testid={`terminal-today-${t.terminal_id}`}>
                    <p className="font-medium">{fmtCurrency(t.takings_today || 0)}</p>
                    <p className="text-xs text-muted-foreground">{t.sales_today || 0} sale{t.sales_today === 1 ? '' : 's'}</p>
                  </TableCell>
                  <TableCell className="text-sm" title={t.last_seen || ''}>
                    {lastContact(t.last_seen_minutes)}
                  </TableCell>
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8" title="Rename"
                      onClick={() => { setRenaming(t); setLabel(t.label || ''); }}
                      data-testid={`rename-terminal-${t.terminal_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8" title="Sign out this terminal"
                      onClick={() => setConfirm({ terminal: t, action: 'sign-out' })}
                      data-testid={`signout-terminal-${t.terminal_id}`}
                    >
                      <LogOut className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Forget this terminal"
                      onClick={() => setConfirm({ terminal: t, action: 'forget' })}
                      data-testid={`forget-terminal-${t.terminal_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RecentHandovers />

      <Dialog open={!!renaming} onOpenChange={() => setRenaming(null)}>        <DialogContent className="max-w-sm" data-testid="rename-terminal-dialog">
          <DialogHeader><DialogTitle>Rename terminal</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Label</Label>
            <Input
              value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Front counter, Till 2" data-testid="terminal-label-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button onClick={saveLabel} disabled={busy || !label.trim()} data-testid="save-terminal-label">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="terminal-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.action === 'sign-out' ? 'Sign out this terminal' : 'Forget this terminal'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirm?.action === 'sign-out'
              ? `${confirm?.terminal?.label} will be rejected the next time it reaches the server, even inside its 14-day offline window. Other terminals keep working, and staff can sign in again on this one.`
              : `${confirm?.terminal?.label} will be removed from the register. If it is still installed it will re-register on its next sign-in — sign it out first if you want it locked out.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button
              variant={confirm?.action === 'forget' ? 'destructive' : 'default'}
              onClick={runAction} disabled={busy} data-testid="confirm-terminal-action"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {confirm?.action === 'sign-out' ? 'Sign out terminal' : 'Forget terminal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
